<template>
  <a-layout-footer class="site-footer">
    <div class="site-footer__inner">
      <div class="site-footer__grid">
        <section class="site-footer__brand">
          <div class="site-footer__brand-name">{{ brandName }}</div>
          <p class="site-footer__tagline">{{ brandTagline }}</p>

          <ul class="site-footer__contact">
            <li v-if="companys.address">
              <EnvironmentOutlined />
              <span>{{ companys.address }}</span>
            </li>
            <li v-if="companys.phone">
              <PhoneOutlined />
              <a :href="`tel:${companys.phone}`">{{ companys.phone }}</a>
            </li>
            <li v-if="companys.email">
              <MailOutlined />
              <a :href="`mailto:${companys.email}`">{{ companys.email }}</a>
            </li>
          </ul>

          <div class="site-footer__social">
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$t('text.facebook')"
            >
              <FacebookOutlined />
            </a>
            <a
              href="https://www.google.com"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$t('text.google')"
            >
              <GoogleOutlined />
            </a>
            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <InstagramOutlined />
            </a>
          </div>
        </section>

        <section
          v-for="(column, index) in footerColumns"
          :key="`${column.title}-${index}`"
          class="site-footer__col"
        >
          <h4>{{ column.title }}</h4>
          <ul>
            <li v-for="(link, linkIndex) in column.links" :key="`${link.to}-${linkIndex}`">
              <router-link :to="link.to">{{ link.label }}</router-link>
            </li>
          </ul>
        </section>
      </div>

      <div class="site-footer__bottom">
        <p>{{ $t('text.copyright') }}</p>
      </div>
    </div>
  </a-layout-footer>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import store from '@/store'
import commons from '@/utils/Common'
import { apiRedis } from '@/api/index'
import { DEFAULT_FOTTER } from '@/const/ApiRedis'
import {
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  FacebookOutlined,
  InstagramOutlined,
  GoogleOutlined,
} from '@ant-design/icons-vue'
import { useI18n } from 'vue-i18n'
import {
  ABOUT,
  CAREERS,
  CART,
  CONTACT,
  DELIVERY_POLICY,
  FAQ_PATH,
  HOME,
  HOW_TO_ORDER,
  LOGIN,
  ORDERS,
  PAYMENT_GUIDE,
  REGISTER,
  RETURN_POLICY,
  SHIPPING_POLICY,
  STORES,
  WARRANTY_GUIDE,
} from '@/const/path'

type FooterLink = { label: string; to: string }
type FooterColumn = { title: string; links: FooterLink[] }

const { t, te } = useI18n()

const FOOTER_LABEL_KEYS: Record<string, string> = {
  'tài khoản': 'text.account',
  'tai khoan': 'text.account',
  'chăm sóc khách hàng': 'text.customerSupport',
  'cham soc khach hang': 'text.customerSupport',
  'đăng nhập': 'text.login',
  'dang nhap': 'text.login',
  'sign in': 'text.login',
  'đăng ký': 'text.register',
  'dang ky': 'text.register',
  'sign up': 'text.register',
  'giỏ hàng': 'text.cart',
  'gio hang': 'text.cart',
  cart: 'text.cart',
  'kiểm tra đơn hàng': 'text.trackOrder',
  'kiem tra don hang': 'text.trackOrder',
  'track order': 'text.trackOrder',
  'check order': 'text.trackOrder',
  'hướng dẫn mua hàng': 'text.howToOrder',
  'huong dan mua hang': 'text.howToOrder',
  'how to order': 'text.howToOrder',
  'hướng dẫn thanh toán': 'text.paymentGuide',
  'huong dan thanh toan': 'text.paymentGuide',
  'payment guide': 'text.paymentGuide',
  'phương thức thanh toán': 'text.paymentMethods',
  'phuong thuc thanh toan': 'text.paymentMethods',
  'chính sách đổi trả': 'text.returnExchangePolicy',
  'chinh sach doi tra': 'text.returnExchangePolicy',
  'return & exchange policy': 'text.returnExchangePolicy',
  'hướng dẫn đổi trả': 'text.returnGuide',
  'huong dan doi tra': 'text.returnGuide',
  'hướng dẫn bảo hành': 'text.warrantyGuide',
  'huong dan bao hanh': 'text.warrantyGuide',
  'warranty guide': 'text.warrantyGuide',
  'chính sách bảo hành': 'text.warrantyPolicy',
  'chinh sach bao hanh': 'text.warrantyPolicy',
  'chính sách giao hàng': 'text.deliveryPolicy',
  'chinh sach giao hang': 'text.deliveryPolicy',
  'delivery policy': 'text.deliveryPolicy',
  'chính sách vận chuyển': 'menu.shippingPolicy',
  'chinh sach van chuyen': 'menu.shippingPolicy',
  'shipping policy': 'menu.shippingPolicy',
  'câu hỏi thường gặp': 'text.faq',
  'cau hoi thuong gap': 'text.faq',
  faq: 'text.faq',
  'giới thiệu': 'text.aboutUs',
  'gioi thieu': 'text.aboutUs',
  'về kima home': 'text.aboutKimaHome',
  've kima home': 'text.aboutKimaHome',
  'about kima home': 'text.aboutKimaHome',
  'hệ thống cửa hàng': 'text.storeLocations',
  'he thong cua hang': 'text.storeLocations',
  'store locations': 'text.storeLocations',
  'khuyến mại': 'text.promotions',
  'khuyen mai': 'text.promotions',
  'tuyển dụng': 'text.careers',
  'tuyen dung': 'text.careers',
  careers: 'text.careers',
  'liên hệ': 'text.contact',
  'lien he': 'text.contact',
  'contact us': 'text.contact',
  'cam kết sản phẩm chính hãng': 'text.authenticCommitment',
  'cam ket san pham chinh hang': 'text.authenticCommitment',
  'chính sách bảo mật': 'text.privacyPolicyTitle',
  'chinh sach bao mat': 'text.privacyPolicyTitle',
  'sản phẩm': 'text.products',
  'san pham': 'text.products',
  'bộ sưu tập': 'text.collections',
  'bo suu tap': 'text.collections',
  'trang chủ': 'text.home',
  'trang chu': 'text.home',
  'đơn hàng': 'text.allOrders',
  'don hang': 'text.allOrders',
}

const normalizeLabel = (label: string) =>
  label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .trim()
    .toLowerCase()

const translateLabel = (label?: string) => {
  if (!label) return ''
  if (label.includes('.')) return te(label) ? t(label) : label
  const key = FOOTER_LABEL_KEYS[normalizeLabel(label)]
  return key ? t(key) : label
}

const linkKey = (label: string, to: string) =>
  `${normalizeLabel(label)}|${(to || HOME).toLowerCase()}`

const companys = ref({
  summary: '',
  sub_name: '',
  address: '',
  phone: '',
  name: '',
  description: '',
  logo: '',
  id: null as number | null,
  email: '',
})

const brandName = computed(() => {
  const raw = companys.value.sub_name || companys.value.name || 'KIMAHOME'
  return String(raw).toUpperCase()
})

const brandTagline = computed(() => {
  const s = (companys.value.summary || '').trim()
  if (s && s.length <= 40) return s.toUpperCase()
  return t('text.luxuryFurniture').toUpperCase()
})

const menuFooter = ref<any[]>([])

const fallbackColumns = computed<FooterColumn[]>(() => [
  {
    title: t('text.account'),
    links: [
      { label: t('text.login'), to: LOGIN },
      { label: t('text.register'), to: REGISTER },
      { label: t('text.cart'), to: CART },
      { label: t('text.trackOrder'), to: ORDERS },
    ],
  },
  {
    title: t('text.customerSupport'),
    links: [
      { label: t('text.contact'), to: CONTACT },
      { label: t('text.howToOrder'), to: HOW_TO_ORDER },
      { label: t('text.paymentGuide'), to: PAYMENT_GUIDE },
      { label: t('text.faq'), to: FAQ_PATH },
      { label: t('text.warrantyGuide'), to: WARRANTY_GUIDE },
    ],
  },
  {
    title: t('text.aboutKimaHome'),
    links: [
      { label: t('text.aboutKimaHome'), to: ABOUT },
      { label: t('text.storeLocations'), to: STORES },
      { label: t('text.returnExchangePolicy'), to: RETURN_POLICY },
      { label: t('menu.shippingPolicy'), to: SHIPPING_POLICY },
      { label: t('text.deliveryPolicy'), to: DELIVERY_POLICY },
      { label: t('text.careers'), to: CAREERS },
    ],
  },
])

const mapLink = (item: any): FooterLink | null => {
  const label = translateLabel(item?.linkLabel || item?.label || item?.name)
  if (!label) return null
  return {
    label,
    to: item?.linkTo || item?.to || item?.path || '/',
  }
}

const dedupeLinks = (links: FooterLink[]) => {
  const seen = new Set<string>()
  return links.filter((link) => {
    const routeKey = (link.to || '/').toLowerCase()
    const key = linkKey(link.label, link.to)
    if (seen.has(key) || [...seen].some((s) => s.endsWith(`|${routeKey}`))) {
      return false
    }
    seen.add(key)
    return true
  })
}

const normalizeMenuColumns = (raw: any[]): FooterColumn[] => {
  if (!Array.isArray(raw) || !raw.length) return []

  const grouped = raw
    .map((menu) => {
      const children = Array.isArray(menu?.children) ? menu.children : []
      const links = dedupeLinks(
        children.map(mapLink).filter(Boolean) as FooterLink[],
      )
      const title = translateLabel(menu?.linkLabel || menu?.label || menu?.name)
      if (!title || !links.length) return null
      return { title, links }
    })
    .filter(Boolean) as FooterColumn[]

  if (grouped.length) return grouped.slice(0, 3)

  const flat = dedupeLinks(raw.map(mapLink).filter(Boolean) as FooterLink[])
  if (!flat.length) return []

  const size = Math.ceil(flat.length / 3) || 1
  const titles = [
    t('text.account'),
    t('text.customerSupport'),
    t('text.aboutKimaHome'),
  ]
  return [0, 1, 2]
    .map((i) => {
      const links = flat.slice(i * size, i * size + size)
      if (!links.length) return null
      return { title: titles[i], links }
    })
    .filter(Boolean) as FooterColumn[]
}

const footerColumns = computed(() => {
  const normalized = normalizeMenuColumns(menuFooter.value)
  return normalized.length ? normalized : fallbackColumns.value
})

onMounted(() => {
  loadMenuFooter()
  cokiesCompany()
})

const cokiesCompany = () => {
  const data = commons.getCokiesCompany()
  if (data) companys.value = data
}

const isLCompanyInfor = computed(() => store.getters.isLCompanyInfor)
watch(isLCompanyInfor, () => {
  cokiesCompany()
})

const loadMenuFooter = async () => {
  try {
    const res: any = await apiRedis(DEFAULT_FOTTER)
    let data = res?.data?.data
    if (typeof data === 'string') data = JSON.parse(data)
    menuFooter.value = Array.isArray(data) ? data : []
  } catch {
    menuFooter.value = []
  }
}
</script>

<style scoped>
.site-footer.ant-layout-footer {
  margin: 0;
  padding: 48px 0 0;
  background: var(--footer-background);
  color: var(--footer-muted);
  font-family: var(--default-font);
  border-top: 1px solid var(--footer-border);
}

.site-footer__inner {
  width: min(1200px, calc(100% - 32px));
  margin: 0 auto;
}

.site-footer__grid {
  display: grid;
  grid-template-columns: 1.35fr repeat(3, minmax(0, 1fr));
  gap: 28px 40px;
  align-items: start;
  padding-bottom: 36px;
}

.site-footer__brand-name {
  margin: 0;
  font-family: var(--heading-font);
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.14em;
  line-height: 1.2;
  color: var(--footer-accent);
}

.site-footer__tagline {
  margin: 6px 0 18px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  color: var(--footer-muted-soft);
}

.site-footer__contact {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.site-footer__contact li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--footer-muted);
}

.site-footer__contact a {
  color: inherit;
  text-decoration: none;
}

.site-footer__contact a:hover {
  color: var(--footer-accent-hover);
}

.site-footer__contact :deep(.anticon) {
  margin-top: 3px;
  color: var(--icon-color);
  font-size: 14px;
}

.site-footer__social {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.site-footer__social a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--footer-border);
  color: var(--footer-muted);
  font-size: 15px;
  transition: 0.2s ease;
}

.site-footer__social a:hover {
  color: var(--footer-accent);
  border-color: var(--footer-accent);
  background: color-mix(in srgb, var(--footer-accent) 14%, transparent);
}

.site-footer__col h4 {
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--footer-border);
  font-family: var(--heading-font);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--footer-color);
}

.site-footer__col ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.site-footer__col a {
  color: var(--footer-muted);
  text-decoration: none;
  font-size: 13px;
  line-height: 1.4;
  transition: color 0.2s ease;
}

.site-footer__col a:hover {
  color: var(--footer-accent-hover);
}

.site-footer__bottom {
  border-top: 1px solid var(--footer-border);
  padding: 16px 0;
  text-align: center;
}

.site-footer__bottom p {
  margin: 0;
  font-size: 12px;
  color: var(--footer-muted-soft);
}

@media (max-width: 991px) {
  .site-footer__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px 24px;
  }

  .site-footer__brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 575px) {
  .site-footer.ant-layout-footer {
    padding-top: 36px;
  }

  .site-footer__grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .site-footer__brand-name {
    font-size: 20px;
  }
}
</style>
